import type { AgentRuntimeSnapshot, AgentType } from "@cord/shared"
import type { NormalizedRawSyncEvent, RawSyncEventBatchApplyResult } from "@/lib/runtime-sync-batch"
import {
  applyClaudeRawSyncEventsBatch,
  buildClaudeSnapshot,
  type ClaudeRawProjection,
} from "@/lib/claude-raw-runtime"
import {
  applyCursorRawSyncEventsBatch,
  buildCursorSnapshot,
  type CursorRawProjection,
} from "@/lib/cursor-raw-runtime"
import {
  applyOpenCodeRawSyncEventsBatch,
  buildOpenCodeSnapshot,
  type OpenCodeRawProjection,
} from "@/lib/opencode-raw-runtime"

export type SupportedSessionRuntimeAgentType = "opencode" | "claude_code" | "cursor"

export interface SessionRuntimeAdapter<TAgent extends SupportedSessionRuntimeAgentType, TRawProjection> {
  agentType: TAgent
  isRawProjectionLike: (value: unknown) => value is TRawProjection
  buildSnapshot: (projection: TRawProjection, eventId: number) => AgentRuntimeSnapshot
  applyBatch: (input: {
    projection: TRawProjection | null
    events: NormalizedRawSyncEvent[]
  }) => RawSyncEventBatchApplyResult<TRawProjection> | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function isOpenCodeProjectionLike(value: unknown): value is OpenCodeRawProjection {
  const row = asRecord(value)
  if (!row) return false
  return (
    asRecord(row.messagesById) !== null &&
    asRecord(row.childSessionToolPartsBySessionId) !== null &&
    asRecord(row.pendingPermissionsById) !== null &&
    asRecord(row.pendingQuestionsById) !== null
  )
}

function isClaudeProjectionLike(value: unknown): value is ClaudeRawProjection {
  const row = asRecord(value)
  if (!row) return false
  return Array.isArray(row.events) && asRecord(row.pendingPermissionsById) !== null
}

function isCursorProjectionLike(value: unknown): value is CursorRawProjection {
  const row = asRecord(value)
  if (!row) return false
  return (
    Array.isArray(row.events) &&
    asRecord(row.pendingPermissionsById) !== null &&
    asRecord(row.pendingQuestionsById) !== null
  )
}

const OPEN_CODE_RUNTIME_ADAPTER: SessionRuntimeAdapter<"opencode", OpenCodeRawProjection> = {
  agentType: "opencode",
  isRawProjectionLike: isOpenCodeProjectionLike,
  buildSnapshot: buildOpenCodeSnapshot,
  applyBatch: ({ projection, events }) =>
    applyOpenCodeRawSyncEventsBatch({
      projection,
      events,
    }),
}

const CLAUDE_RUNTIME_ADAPTER: SessionRuntimeAdapter<"claude_code", ClaudeRawProjection> = {
  agentType: "claude_code",
  isRawProjectionLike: isClaudeProjectionLike,
  buildSnapshot: buildClaudeSnapshot,
  applyBatch: ({ projection, events }) =>
    applyClaudeRawSyncEventsBatch({
      projection,
      events,
    }),
}

const CURSOR_RUNTIME_ADAPTER: SessionRuntimeAdapter<"cursor", CursorRawProjection> = {
  agentType: "cursor",
  isRawProjectionLike: isCursorProjectionLike,
  buildSnapshot: buildCursorSnapshot,
  applyBatch: ({ projection, events }) =>
    applyCursorRawSyncEventsBatch({
      projection,
      events,
    }),
}

export const SESSION_RUNTIME_ADAPTERS = {
  opencode: OPEN_CODE_RUNTIME_ADAPTER,
  claude_code: CLAUDE_RUNTIME_ADAPTER,
  cursor: CURSOR_RUNTIME_ADAPTER,
} as const

export function isSupportedSessionRuntimeAgentType(
  value: AgentType | null | undefined,
): value is SupportedSessionRuntimeAgentType {
  return value === "opencode" || value === "claude_code" || value === "cursor"
}

export function getSessionRuntimeAdapter(
  agentType: AgentType | null | undefined,
): SessionRuntimeAdapter<SupportedSessionRuntimeAgentType, unknown> | null {
  if (!isSupportedSessionRuntimeAgentType(agentType)) return null
  return SESSION_RUNTIME_ADAPTERS[agentType] as SessionRuntimeAdapter<
    SupportedSessionRuntimeAgentType,
    unknown
  >
}
